import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import {
    checkIfDateIsValidDate,
    addDaysToDate,
    addWeeksToDate,
    addMonthsToDate,
    addYearsToDate,
    setSpecificDayOfMonthOnDate,
    parseDateStringAsDateObject,
    checkIfDateIsAfterOtherDate,
    checkIfDateIsBeforeOtherDate,
    printDateObjectAsFormattedString,
    checkIfTwoDatesAreOnTheSameDay,
    checkIfTwoDatesAreInTheSameMonth,
} from '../../helpers';
import CalendarContent from './CalendarContent';
import s from './Calendar.css';

// Types
/**
 * Grid day object
 * @typedef GridDay
 * @property {number} id
 * @property {Date} date
 * @property {string} dateString
 * @property {boolean} isSelected
 * @property {boolean} isCurrentMonth
 * @property {boolean} isToday
 * @property {boolean} isDisabled
 * @property {('older-than-max-date' | 'younger-than-min-date' | null)} isDisabledBecause
 */

/**
 * Grid month object
 * @typedef {Object} GridMonth
 * @property {string} id
 * @property {number} month
 * @property {string} monthString
 * @property {boolean} isSelected
 */

/**
 * Grid row object
 * @typedef {Object} GridRow
 * @property {string} key
 * @property {GridDay[]}
 */

// Props
const propTypes = {
    onChange: PropTypes.func,
    className: PropTypes.string,
    value: PropTypes.object,
    defaultValue: PropTypes.object,
    maxDate: PropTypes.object,
    minDate: PropTypes.object,
};

// Component
class CalendarContainer extends React.Component {
    static defaultProps = {
        onChange: undefined,
        className: undefined,
        value: null,
        defaultValue: null,
        maxDate: undefined,
        minDate: undefined,
    };

    constructor(props) {
        super(props);

        this.weekDayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        this.state = {
            showDays: true,
            displayedDate:
                typeof this.props.value !== 'undefined' &&
                this.props.value !== null &&
                checkIfDateIsValidDate(this.props.value)
                    ? this.props.value
                    : new Date(),
        };
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (
            this.props.value !== nextProps.value ||
            this.props.className !== nextProps.className ||
            this.props.minDate !== nextProps.minDate ||
            this.props.maxDate !== nextProps.maxDate ||
            this.state.showDays !== nextState.showDays ||
            this.state.displayedDate !== nextState.displayedDate
        ) {
            return true;
        } else {
            return false;
        }
    }

    componentDidMount() {
        // Set initial date value if main value is missing
        if (typeof this.props.value === 'undefined' && typeof this.props.defaultValue !== 'undefined') {
            this.prepareInitialDate(this.props.value, this.props.defaultValue);
        }
    }

    componentWillUnmount() {
        this.isReady = false;
    }

    render() {
        return (
            <CalendarContent
                className={this.props.className}
                maxDate={this.props.maxDate}
                minDate={this.props.minDate}
                value={this.props.value}
                showDays={this.state.showDays}
                displayedDate={this.state.displayedDate}
                weekDayNames={this.weekDayNames}
                onDateChange={this.handleDateChange}
                onDisplayedMonthChange={this.handleDisplayedMonthChange}
                onPeriodArrowClick={this.handlePeriodArrowClick}
                onDaysMonthsToggle={this.handleDaysMonthsToggle}
            />
        );
    }

    prepareInitialDate = (value, defaultValue) => {
        if (value !== null || defaultValue === null) {
            return;
        }

        if (typeof this.props.onChange === 'undefined' || this.props.onChange === null) {
            return;
        }

        if (typeof defaultValue === 'string') {
            const today = new Date();
            if (defaultValue === 'tomorrow') {
                this.onChangeCallback(addDaysToDate(today, 1));
            } else if (defaultValue === 'inaweek') {
                this.onChangeCallback(addWeeksToDate(today, 1));
            } else if (defaultValue === 'inamonth') {
                this.onChangeCallback(addMonthsToDate(today, 1));
            } else if (defaultValue === 'inayear') {
                this.onChangeCallback(addYearsToDate(today, 1));
            } else {
                this.onChangeCallback(today);
            }
            return;
        }

        if (checkIfDateIsValidDate(defaultValue)) {
            this.onChangeCallback(defaultValue);
        }
    };

    /**
     * Forward selected date to parent component
     * @param {(Date | null)} date
     */
    onChangeCallback = date => {
        if (this.props.onChange) {
            this.props.onChange(date);
        }
    };

    /**
     * Handle left and right arrow clicks
     * @param {('left' | 'right')} direction
     */
    handlePeriodArrowClick = direction => {
        // Calculate next date
        let nextDisplayedDate = setSpecificDayOfMonthOnDate(this.state.displayedDate, 15);
        if (this.state.showDays) {
            nextDisplayedDate =
                direction === 'right' ? addMonthsToDate(nextDisplayedDate, 1) : addMonthsToDate(nextDisplayedDate, -1);
        } else {
            nextDisplayedDate =
                direction === 'right' ? addYearsToDate(nextDisplayedDate, 1) : addYearsToDate(nextDisplayedDate, -1);
        }

        // Change next displayed date
        this.setState({
            displayedDate: nextDisplayedDate,
        });
    };

    /**
     * Handle toggling between days and months view
     */
    handleDaysMonthsToggle = e => {
        this.setState({
            showDays: !this.state.showDays,
        });
    };

    /**
     * Handle changing date by clicking on its value
     * @param {string} dateString
     */
    handleDateChange = dateString => e => {
        const date = parseDateStringAsDateObject(dateString);
        if (checkIfDateIsValidDate(date)) {
            this.setState({
                showDays: true,
                displayedDate: date,
            });

            this.onChangeCallback(date);
        }
    };

    /**
     * Handle changing displayed month by clicking on its value
     * @param {string} dateString
     */
    handleDisplayedMonthChange = dateString => e => {
        const date = parseDateStringAsDateObject(dateString + '-15');
        if (checkIfDateIsValidDate(date)) {
            this.setState({
                showDays: true,
                displayedDate: date,
            });
        }
    };
}

CalendarContainer.propTypes = propTypes;

export const Calendar = connect()(CalendarContainer);



// WEBPACK FOOTER //
// ./app/web/src/components/fields/Calendar.jsx